import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { usePageLanguage, useLocalizedPath, SUPPORTED_LANGS } from '@/hooks/useLanguage';
import { useSeo } from '@/hooks/useSeo';
import { getKeyPrefix, relatedArticles } from '@/mocks/articles';
import JsonLd from '@/components/base/JsonLd';
import { SITE_NAME, FAVICON_URL, absoluteUrl } from '@/lib/site';

const sections = [
  { id: 's1', paras: ['p1', 'p2'] },
  { id: 's2', paras: ['p1', 'p2'] },
  { id: 's3', paras: ['p1', 'p2'] },
  { id: 's4', paras: ['p1', 'p2'] },
];

const tableRows = [
  ['r1c1', 'r1c2', 'r1c3', 'r1c4'],
  ['r2c1', 'r2c2', 'r2c3', 'r2c4'],
  ['r3c1', 'r3c2', 'r3c3', 'r3c4'],
  ['r4c1', 'r4c2', 'r4c3', 'r4c4'],
];

export default function Article({ slug }: { slug: string }) {
  const { t, i18n } = useTranslation();
  usePageLanguage();
  useSeo(slug);
  const localize = useLocalizedPath();
  const prefix = getKeyPrefix(slug);

  const lang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : 'en';
  const articleUrl = absoluteUrl(localize(`/${slug}`));

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t(`${prefix}.h1`),
    description: t(`${prefix}.lead`),
    inLanguage: lang,
    url: articleUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
    datePublished: '2026-08-21',
    dateModified: '2026-08-21',
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: FAVICON_URL },
    },
  };

  useEffect(() => {
    document.title = t(`${prefix}.h1`);
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    html.style.scrollBehavior = prev;
  }, [t, i18n.language, prefix]);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleJsonLd} />
      <Navbar />

      <main className="mx-auto max-w-3xl px-4 pb-12 pt-24 md:px-6 md:pb-16 md:pt-28">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-foreground-500 md:text-sm">
          <Link to={localize('/')} className="transition-colors hover:text-primary-600">
            {t('article.breadcrumb')}
          </Link>
          <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
          <span className="text-foreground-700">{t(`${prefix}.category`)}</span>
        </nav>

        {/* Header */}
        <header className="mt-6">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
              {t(`${prefix}.category`)}
            </span>
            <span className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
              {t(`${prefix}.readTime`)}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-semibold leading-tight text-foreground-950 md:text-4xl md:leading-tight">
            {t(`${prefix}.h1`)}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">
            {t(`${prefix}.lead`)}
          </p>
        </header>

        {/* Table of contents */}
        <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
          <h4 className="text-sm font-semibold text-foreground-950">{t(`${prefix}.toc`)}</h4>
          <ul className="mt-3 space-y-2 text-sm">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-foreground-600 transition-colors hover:text-primary-600">
                  {t(`${prefix}.${s.id}.title`)}
                </a>
              </li>
            ))}
            <li>
              <a href="#s5" className="text-foreground-600 transition-colors hover:text-primary-600">
                {t(`${prefix}.s5.title`)}
              </a>
            </li>
            <li>
              <a href="#s6" className="text-foreground-600 transition-colors hover:text-primary-600">
                {t(`${prefix}.s6.title`)}
              </a>
            </li>
            <li>
              <a href="#s7" className="text-foreground-600 transition-colors hover:text-primary-600">
                {t(`${prefix}.s7.title`)}
              </a>
            </li>
            <li>
              <a href="#conclusion" className="text-foreground-600 transition-colors hover:text-primary-600">
                {t(`${prefix}.conclusion.title`)}
              </a>
            </li>
          </ul>
        </div>

        {/* Sections */}
        <article className="mt-10 space-y-10">
          {sections.map((s) => (
            <section key={s.id} id={s.id}>
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                {t(`${prefix}.${s.id}.title`)}
              </h2>
              {s.paras.map((p) => (
                <p key={p} className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {t(`${prefix}.${s.id}.${p}`)}
                </p>
              ))}
            </section>
          ))}

          {/* Comparison table section */}
          <section id="s5">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {t(`${prefix}.s5.title`)}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t(`${prefix}.s5.p1`)}
            </p>
            <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full min-w-[560px] border-collapse text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="border-b border-background-200 px-4 py-3 text-left font-semibold text-foreground-900">
                      {t(`${prefix}.table.c1`)}
                    </th>
                    <th className="border-b border-background-200 px-4 py-3 text-left font-semibold text-foreground-900">
                      {t(`${prefix}.table.c2`)}
                    </th>
                    <th className="border-b border-background-200 px-4 py-3 text-left font-semibold text-foreground-900">
                      {t(`${prefix}.table.c3`)}
                    </th>
                    <th className="border-b border-background-200 px-4 py-3 text-left font-semibold text-foreground-900">
                      {t(`${prefix}.table.c4`)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 1 ? 'bg-background-50' : 'bg-background-100/50'}>
                      {row.map((cell) => (
                        <td
                          key={cell}
                          className={`border-b border-background-200 px-4 py-3 text-foreground-600 ${
                            cell.endsWith('c1') ? 'font-semibold text-foreground-900' : ''
                          }`}
                        >
                          {t(`${prefix}.table.${cell}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Extra section 6 */}
          <section id="s6">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {t(`${prefix}.s6.title`)}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t(`${prefix}.s6.p1`)}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t(`${prefix}.s6.p2`)}
            </p>
          </section>

          {/* Extra section 7 */}
          <section id="s7">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {t(`${prefix}.s7.title`)}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t(`${prefix}.s7.p1`)}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t(`${prefix}.s7.p2`)}
            </p>
          </section>

          {/* Conclusion */}
          <section id="conclusion">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {t(`${prefix}.conclusion.title`)}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t(`${prefix}.conclusion.p1`)}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t(`${prefix}.conclusion.p2`)}
            </p>
          </section>
        </article>

        {/* Related articles */}
        <div className="mt-12">
          <h3 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">
            {t('article.related')}
          </h3>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {(relatedArticles[slug] ?? []).map((relSlug) => {
              const relPrefix = getKeyPrefix(relSlug);
              return (
                <Link
                  key={relSlug}
                  to={localize(`/${relSlug}`)}
                  className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-all hover:-translate-y-0.5 hover:border-primary-300"
                >
                  <span className="flex items-center gap-1 text-xs font-medium text-primary-600">
                    <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center"></i>
                    {t(`${relPrefix}.category`)}
                  </span>
                  <p className="mt-2 text-sm font-semibold leading-snug text-foreground-900 group-hover:text-primary-600">
                    {t(`${relPrefix}.shortTitle`)}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-lg border border-background-200 bg-gradient-to-br from-primary-50 to-accent-50 p-8 text-center">
          <p className="text-sm text-foreground-600 md:text-base">{t(`${prefix}.conclusion.p2`)}</p>
          <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              {t(`${prefix}.cta`)}
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <Link
              to={localize('/')}
              className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              {t(`${prefix}.backHome`)}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}